import * as _ from 'underscore';
import { ListResult } from '../fetchers/ListResult';
import { ISliceable } from '../models/ISliceable';
import { MaxRequestArrayLength, RequestHelper } from '../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { ITypeaheadSource } from '../models/ITypeaheadSource';

export module SliceableHelper {

  export let BlockSize = MaxRequestArrayLength;

  // Return true from itemsReceived to cancel.
  export function fetchAll<T>(sliceable: ISliceable<T>, itemsReceived?: (items: T[]) => boolean): Promise<ListResult<T>> {
    let totalList: T[] = [];

    const getBlock = (blockStartIndex: number = 0): Promise<ListResult<T>> => {
      return sliceable.getSlice(blockStartIndex, BlockSize).then((result: ListResult<T>): any => { // Should return either Promise<ListResult<T>> or ListResult<T>
        let cancel = false;
        if (itemsReceived) {
          cancel = !!itemsReceived(result.items);
        }
        totalList = totalList.concat(result.items);
        if(!cancel && result.total > totalList.length) {
          return getBlock(totalList.length);
        }
        return new ListResult(totalList);
      });
    };

    return getBlock();
  }

  /**
   * This helper allows for fetching all items for a query where one of the parameters is an array
   * filter (e.g. fetch all data connections where data source ID is IN a given list of IDs).
   * For this kind of query (particularly where there's a one-to-many relationship between the input
   * array param values and the returned objects), we need to break down the request in two ways:
   *   1. Break the request's array param into chunks of 1000 items to pass into the API;
   *   2. Fetch multiple pages of result items (a sliceable query typically returns 100 at a time).
   *
   * Because the sliceable query is being distributed over multiple chunks and reassembled after the
   * query is executed, this method does not guarantee that sort parameters in the original query
   * will be honored in the final list.
   */
  export function fetchAllForSliceableWithArrayParam<TArrayParam, T>(
    arrayParamValues: TArrayParam[],
    makeSliceableFromArrayParamBlock: (arrayParamValues: TArrayParam[]) => ISliceable<T>
  ): Promise<T[]> {

    const arrayParamBlocks = RequestHelper.makeBlocks(arrayParamValues, RequestHelper.DefaultBlockSize);

    const resultPromisesPerBlock: Promise<ListResult<T>>[] = arrayParamBlocks.map(block => {
      const sliceable = makeSliceableFromArrayParamBlock(block);
      return SliceableHelper.fetchAll(sliceable);
    });

    return Promise.all(resultPromisesPerBlock).then(ResultHelper.combineListResultItems);
  }

  export function fromArray<T>(list: T[] = []): ISliceable<T> {
    return {
      getSlice: (start: number, size: number): Promise<ListResult<T>> => {
        const slice = list.slice(start, start + size);
        return Promise.resolve(new ListResult(slice, start, list.length));
      }
    };
  }

  export function searchArray<T>(searchFilterFn: (item: T, search: string) => boolean, listPromise: Promise<T[]>): ITypeaheadSource {
    let list: T[];

    return (search: string) => {
      let filteredSliceable: ISliceable<T>;

      const createFilteredSliceable = () => {
        let filteredList = _.filter(list, (item: T) => searchFilterFn(item, search));

        // Angular tries to optimize ng-repeats by adding a "$$hashKey property to objects to prevent re-rendering
        // items in a list that have not changed.  This breaks the tbTypeaheadFormat directive that relies on re-rendering
        // each item in the list so we need to pass in "fresh" models every time the search changes.
        filteredList = _.map(filteredList, _.clone);

        filteredSliceable = fromArray(filteredList);
      };
      createFilteredSliceable();

      return {
        getSlice: (start: number, size: number): Promise<ListResult<T>> => {
          if(list) return filteredSliceable.getSlice(start, size);

          return listPromise.then((newList: T[]) => {
            list = newList;
            createFilteredSliceable();
            return filteredSliceable.getSlice(start, size);
          });
        }
      };
    };
  }

}
